import type React from 'react';
import { type ReactNode, useRef } from 'react';
import classnames from 'classnames';

import Icon from '../../../../primitives/icon/index';
import { useScrollableNav } from '../../hooks/use-scrollable-nav';

import styles from './ScrollableNav.module.css';

interface ScrollableNavProps {
  autoScrollToSelected?: boolean;
  children: ReactNode;
  className?: string;
  navItemsClassName?: string;
  navigationKey?: string;
  style?: React.CSSProperties;
  testId?: string;
}

export const ScrollableNav = ({
  autoScrollToSelected = false,
  children,
  className,
  navItemsClassName,
  navigationKey,
  style,
  testId,
}: ScrollableNavProps) => {
  const navItemsRef = useRef<HTMLUListElement>(null);

  const { scrollNav, showLeftArrow, showRightArrow } = useScrollableNav({
    autoScrollToSelected,
    navigationKey,
    navItemsRef,
  });

  return (
    <div
      className={classnames(styles.wrapper, className, {
        [styles.showLeftFade]: showLeftArrow,
        [styles.showRightFade]: showRightArrow,
      })}
      data-testid={testId}
      style={style}
    >
      <div className={styles.navItemsContainer}>
        <button
          aria-hidden={!showLeftArrow}
          aria-label="Scroll left"
          className={classnames(styles.scrollArrow, styles.scrollArrowLeft, {
            [styles.scrollArrowHidden]: !showLeftArrow,
          })}
          data-testid={`${testId}-scroll-left`}
          disabled={!showLeftArrow}
          onClick={() => scrollNav('left')}
          tabIndex={showLeftArrow ? 0 : -1}
          type="button"
        >
          <Icon height={16} icon="ChevronsLeft" width={16} />
        </button>
        <ul className={classnames(styles.navItems, navItemsClassName)} ref={navItemsRef}>
          {children}
        </ul>
        <button
          aria-hidden={!showRightArrow}
          aria-label="Scroll right"
          className={classnames(styles.scrollArrow, styles.scrollArrowRight, {
            [styles.scrollArrowHidden]: !showRightArrow,
          })}
          data-testid={`${testId}-scroll-right`}
          disabled={!showRightArrow}
          onClick={() => scrollNav('right')}
          tabIndex={showRightArrow ? 0 : -1}
          type="button"
        >
          <Icon height={16} icon="ChevronsRight" width={16} />
        </button>
      </div>
    </div>
  );
};
